import { useState, useMemo } from 'react';

export interface PeopleManagementState {
  search: string;
  filter: string;
  sort: string;
}

export interface PeopleData {
  face_label: string | null;
  gcs_path: string;
  person_id: string;
}

export type UsePeopleManagementState = {
  state: PeopleManagementState;
  filteredAndSortedPeople: PeopleData[];
  updateSearch: (search: string) => void;
  updateFilter: (filter: string) => void;
  updateSort: (sort: string) => void;
};

export const usePeopleManagementState = (peopleData: PeopleData[] = []) :  UsePeopleManagementState=> {
  const [state, setState] = useState<PeopleManagementState>({
    search: '',
    filter: 'all',
    sort: 'name_asc',
  });

  const filteredAndSortedPeople = useMemo(() => {
    let filtered = [...peopleData];

    // Apply search filter
    if (state.search.trim()) {
      const searchTerm = state.search.toLowerCase();
      filtered = filtered.filter((person) => {
        const name = person.face_label || 'unnamed';
        return name.toLowerCase().includes(searchTerm);
      });
    }

    // Apply category filter
    switch (state.filter) {
      case 'labeled':
        filtered = filtered.filter((person) => person.face_label !== null);
        break;
      case 'unlabeled':
        filtered = filtered.filter((person) => person.face_label === null);
        break;
      case 'all':
      default:
        // No additional filtering needed
        break;
    }

    // Apply sorting
    switch (state.sort) {
      case 'name_asc':
        filtered.sort((a, b) => {
          const nameA = a.face_label || 'unnamed';
          const nameB = b.face_label || 'unnamed';
          return nameA.localeCompare(nameB);
        });
        break;
      case 'name_desc':
        filtered.sort((a, b) => {
          const nameA = a.face_label || 'unnamed';
          const nameB = b.face_label || 'unnamed';
          return nameB.localeCompare(nameA);
        });
        break;
      case 'recent':
      default:
        // Keep original order for "most recent"
        break;
    }

    return filtered;
  }, [peopleData, state.search, state.filter, state.sort]);

  const updateSearch = (search: string): void => {
    setState((prev) => ({ ...prev, search }));
  };

  const updateFilter = (filter: string): void => {
    setState((prev) => ({ ...prev, filter }));
  };

  const updateSort = (sort: string): void => {
    setState((prev) => ({ ...prev, sort }));
  };

  return {
    state,
    filteredAndSortedPeople,
    updateSearch,
    updateFilter,
    updateSort,
  };
};
